import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // STANLEY CUSTOM: inicializar el sibling del video en el grid
    MediaGallery.initVideoSibling(this);

    // STANLEY CUSTOM: corregir span 2 del primer item visible (large_first_image)
    MediaGallery.initFirstGridItem(this);

    // STANLEY CUSTOM: reproducir video del grid tras cambio de variante
    MediaGallery.initVideoAutoplay(this);

    // STANLEY CUSTOM: ocultar en el zoom dialog los items de otras variantes
    MediaGallery.initZoomDialog(this);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event.
   *
   * STANLEY CUSTOM (fix flash): NO reemplazamos la galería. Las medias de todas
   * las variantes ya están pre-renderizadas en el DOM y se filtran client-side
   * por alt text (filterMediaByColor en theme.liquid). El `replaceWith` original
   * destruía las imágenes ya decodificadas y forzaba re-decodificarlas → flash.
   *
   * En su lugar:
   *   1) Disparamos `stanley:variant-media-update` para que theme.liquid aplique
   *      el filtro (show/hide). El `span 2` de la hero lo maneja directamente
   *      `filterMediaByColor` (theme.liquid), co-localizado con la decisión de
   *      visibilidad → determinístico, sin la carrera de `initFirstGridItem`
   *      (que medía por rect width y a veces elegía el li viejo aún no oculto).
   *   2) Tras el filtro, re-corremos sobre ESTE elemento (persistente) lo demás
   *      que depende de visibilidad: `initVideoSibling` (blur, ahora idempotente)
   *      e `initZoomDialog`.
   *   `initVideoAutoplay` NO se re-corre: como ya no se destruye ningún nodo, sus
   *   observers siguen vivos; re-correrlo duplicaría observers sobre los mismos
   *   wrappers.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    // Mantenemos la validación de respuesta válida, pero ya NO usamos el nodo
    // (no hacemos replaceWith).
    if (!newMediaGallery) return;

    // 1) Filtro determinístico vía theme.liquid (incluye el span 2 de la hero).
    document.dispatchEvent(new CustomEvent('stanley:variant-media-update'));

    // 2) Re-aplicar lo dependiente de visibilidad, con doble rAF para medir ya filtrado.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        MediaGallery.initVideoSibling(this);
        MediaGallery.initZoomDialog(this);
      })
    );
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    // STANLEY CUSTOM: las vistas 360° van primero en el slideshow pero no están en el zoom.
    const offset = this.querySelectorAll('slideshow-slide.product-media-container--vista360').length;
    this.slideshow?.select(event.detail.index + offset, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * STANLEY CUSTOM: Detecta la imagen "sibling" del video en el grid de 2 columnas
   * y le aplica la clase + CSS vars necesarias para el efecto de blur.
   * Solo aplica en desktop (≥750px) y en modo grid.
   *
   * Usa IntersectionObserver para esperar a que el grid sea visible antes de
   * calcular posiciones — resuelve el problema de timing en la carga inicial
   * cuando el video está debajo del fold.
   *
   * @param {MediaGallery} galleryEl - El elemento media-gallery a procesar.
   */
  static initVideoSibling(galleryEl) {
    if (window.innerWidth < 750) return;
    if (!galleryEl.classList.contains('media-gallery--grid')) return;
    if (!galleryEl.classList.contains('media-gallery--two-column')) return;

    const grid = galleryEl.querySelector('.media-gallery__grid');
    if (!grid) return;

    const apply = () => {
      const allItems = Array.from(grid.querySelectorAll('li.product-media-container'));
      const visibleItems = allItems.filter((el) => el.getBoundingClientRect().width > 0);

      let videoItem = null;
      for (let i = visibleItems.length - 1; i >= 0; i--) {
        if (visibleItems[i].classList.contains('product-media-container--video')) {
          videoItem = visibleItems[i];
          break;
        }
      }
      if (!videoItem) return;

      const videoRect = videoItem.getBoundingClientRect();

      let siblingItem = null;
      for (let j = visibleItems.length - 1; j >= 0; j--) {
        const item = visibleItems[j];
        if (item === videoItem) continue;
        const r = item.getBoundingClientRect();
        if (Math.abs(r.top - videoRect.top) < 10 && r.left < videoRect.left) {
          siblingItem = item;
          break;
        }
      }
      if (!siblingItem) return;

      allItems.forEach((el) => el.classList.remove('product-media-container--video-sibling'));

      const videoAspect = parseFloat(videoItem.style.getPropertyValue('--gallery-aspect-ratio')) || 0.5625;
      const img = siblingItem.querySelector('img.product-media__image');
      const imgSrc = img ? img.getAttribute('src') : '';

      siblingItem.classList.add('product-media-container--video-sibling');
      siblingItem.style.setProperty('--video-aspect-ratio', videoAspect);

      const productMedia = siblingItem.querySelector('.product-media');
      if (productMedia && imgSrc) {
        productMedia.style.setProperty('--sibling-bg-url', 'url(' + imgSrc + ')');
      }
    };

    // Intentar aplicar inmediatamente con doble rAF (para cambios de variante
    // donde el layout ya está calculado)
    requestAnimationFrame(() => requestAnimationFrame(apply));

    // Además, observar el grid con IntersectionObserver para cuando esté
    // fuera del viewport en la carga inicial.
    // STANLEY CUSTOM (fix flash): idempotente. Como ahora initVideoSibling se
    // re-ejecuta en cada cambio de variante sobre el MISMO elemento (sin
    // replaceWith), desconectamos el observer previo antes de crear uno nuevo
    // para no acumular observers.
    if (galleryEl.__videoSiblingObserver) {
      galleryEl.__videoSiblingObserver.disconnect();
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          apply();
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    galleryEl.__videoSiblingObserver = observer;
    observer.observe(grid);
  }

  /**
   * STANLEY CUSTOM: Corrige el grid-column: span 2 del primer item visible
   * en galerías con large_first_image activo.
   *
   * @param {MediaGallery} galleryEl - El elemento media-gallery a procesar.
   */
  static initFirstGridItem(galleryEl) {
    if (!galleryEl.classList.contains('media-gallery--large-first-image')) return;
    if (!galleryEl.classList.contains('media-gallery--two-column')) return;
    if (!galleryEl.classList.contains('media-gallery--grid')) return;

    const grid = galleryEl.querySelector('.media-gallery__grid');
    if (!grid) return;

    // Doble rAF para asegurar que el layout está completamente calculado
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const allItems = Array.from(grid.querySelectorAll('li.product-media-container'));

        allItems.forEach((el) => {
          if (el.style.gridColumn === 'span 2') el.style.gridColumn = '';
        });

        const firstVisible = allItems.find((el) => el.getBoundingClientRect().width > 0);
        if (!firstVisible) return;

        firstVisible.style.gridColumn = 'span 2';
      })
    );
  }

  /**
   * STANLEY CUSTOM: Maneja el play/pause del video del grid en la PDP.
   *
   * Reemplaza el script inline de custom-product-media.liquid que no se ejecuta
   * cuando el HTML es insertado via replaceWith (limitación del browser).
   *
   * Replica la misma lógica del script inline:
   * - MutationObserver en aria-hidden del li/slide contenedor (modo carousel)
   * - IntersectionObserver en el wrapper del video (modo grid y fallback)
   *
   * @param {MediaGallery} galleryEl - El elemento media-gallery a procesar.
   */
  static initVideoAutoplay(galleryEl) {
    const wrappers = galleryEl.querySelectorAll('.product-media__video-wrapper');
    if (!wrappers.length) return;

    wrappers.forEach((wrapper) => {
      const vid = wrapper.querySelector('video');
      if (!vid) return;

      const playVideo = () => {
        if (!vid.readyState) vid.load();
        vid.play().catch(() => {});
      };

      const pauseVideo = () => {
        vid.pause();
      };

      // MutationObserver en el slide/li contenedor (modo carousel)
      const slide = wrapper.closest('slideshow-slide, li');
      if (slide) {
        const slideObserver = new MutationObserver(() => {
          const hidden = slide.getAttribute('aria-hidden');
          if (hidden === 'false' || hidden === null) {
            playVideo();
          } else {
            pauseVideo();
          }
        });
        slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
        // Estado inicial
        if (slide.getAttribute('aria-hidden') !== 'true') {
          playVideo();
        }
      }

      // IntersectionObserver como fallback y para modo grid
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              playVideo();
            } else {
              pauseVideo();
            }
          });
        },
        { threshold: 0.5 }
      );
      io.observe(wrapper);
    });
  }
  /**
   * STANLEY CUSTOM: Sincroniza el zoom dialog con la variante activa.
   *
   * El zoom dialog renderiza los medios de todas las variantes en el DOM.
   * Al navegar entre imágenes en el zoom, se ven las imágenes intermedias de
   * otras variantes. Este método las oculta, dejando solo las de la variante activa.
   *
   * Se basa en los mediaIds visibles del grid (que ya filtra correctamente por variante)
   * para determinar qué ítems del zoom dialog mostrar u ocultar.
   *
   * Corre en connectedCallback (carga inicial) y en cada cambio de variante
   * (re-invocado desde #handleVariantUpdate tras el filtro, ya sin replaceWith).
   *
   * @param {MediaGallery} galleryEl - El elemento media-gallery a procesar.
   */
  static initZoomDialog(galleryEl) {
    const grid = galleryEl.querySelector('.media-gallery__grid');
    const zoomDialog = galleryEl.querySelector('zoom-dialog');
    if (!grid || !zoomDialog) return;

    const apply = () => {
      const allGridItems = Array.from(grid.querySelectorAll('li.product-media-container'));

      // Filtrar items visibles: primero por getBoundingClientRect (más preciso),
      // con fallback a display != none (funciona aunque el grid esté fuera del viewport)
      let visibleItems = allGridItems.filter((el) => el.getBoundingClientRect().width > 0);
      if (!visibleItems.length) {
        visibleItems = allGridItems.filter((el) => getComputedStyle(el).display !== 'none');
      }

      const visibleMediaIds = new Set(
        visibleItems
          .map((el) => el.querySelector('[data-media-id]')?.getAttribute('data-media-id'))
          .filter(Boolean)
      );

      if (!visibleMediaIds.size) return;

      const zoomItems = Array.from(zoomDialog.querySelectorAll('.dialog-zoomed-gallery li'));
      const thumbnailItems = Array.from(zoomDialog.querySelectorAll('.dialog-thumbnails-list button'));

      zoomItems.forEach((li, i) => {
        const mediaId = li.querySelector('[data-media-id]')?.getAttribute('data-media-id');
        li.style.display = visibleMediaIds.has(mediaId) ? '' : 'none';

        const btn = thumbnailItems[i];
        if (btn) {
          btn.style.display = visibleMediaIds.has(mediaId) ? '' : 'none';
        }
      });
    };

    // Doble rAF para asegurar layout calculado al conectar
    requestAnimationFrame(() => requestAnimationFrame(apply));
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}